// Shared formatter that renders a job's scheduled_at consistently in the
// business timezone (America/Los_Angeles) regardless of the visitor's browser.
//
// Handles the two ISO shapes stored in the DB:
//   1. tz-aware  ("2026-08-27T11:00:00-07:00", "...+00:00", "...Z")
//      → parse as an absolute instant, format in America/Los_Angeles.
//   2. tz-naive  ("2026-08-24T10:00:00")  ← admin datetime-local input + seed
//      → the naive components ARE the business-local wall time.
//        Construct via Date.UTC(...) and format with timeZone:"UTC" so the
//        exact wall-clock components (10:00) are preserved for any browser.
const BIZ_TZ = "America/Los_Angeles";
const OFFSET_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;
const NAIVE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

function _format(iso, opts) {
  if (!iso) return "";
  try {
    if (!OFFSET_RE.test(iso)) {
      const m = iso.match(NAIVE_ISO_RE);
      if (m) {
        const [, Y, Mo, D, H, Mi, S] = m;
        const d = new Date(Date.UTC(+Y, +Mo - 1, +D, +H, +Mi, +(S || 0)));
        return d.toLocaleString([], { ...opts, timeZone: "UTC" });
      }
    }
    return new Date(iso).toLocaleString([], { ...opts, timeZone: BIZ_TZ });
  } catch { return iso; }
}

export const formatBizDateTime = (iso) => _format(iso, {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});
export const formatBizDateTimeLong = (iso) => _format(iso, {
  weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});
export const formatBizHour = (iso) => _format(iso, { hour: "numeric" });

// Return the Pacific business date "YYYY-MM-DD" for any scheduled_at ISO string.
// Used for calendar bucketing so a job stored as "2026-08-27T05:11:06+00:00"
// (which is Aug 26 22:11 Pacific) correctly buckets under 2026-08-26.
const _dateKeyFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: BIZ_TZ, year: "numeric", month: "2-digit", day: "2-digit",
});
export function bizDateKey(iso) {
  if (!iso) return "";
  // Naive ISO → business-local wall time; extract Y-M-D directly.
  if (!OFFSET_RE.test(iso)) {
    const m = iso.match(NAIVE_ISO_RE);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    return "";
  }
  // tz-aware → convert to Pacific via Intl and re-assemble.
  try {
    const parts = _dateKeyFmt.formatToParts(new Date(iso)).reduce((a, p) => {
      a[p.type] = p.value; return a;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch { return ""; }
}

// Return a value suitable for <input type="datetime-local"> ("YYYY-MM-DDTHH:MM")
// representing the given ISO string in Pacific wall-clock time. Admins outside
// Pacific still see and edit the Pacific hour of the appointment.
const _localInputFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: BIZ_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
export function bizLocalInputValue(iso) {
  if (!iso) return "";
  if (!OFFSET_RE.test(iso)) {
    const m = iso.match(NAIVE_ISO_RE);
    if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
    return "";
  }
  try {
    const parts = _localInputFmt.formatToParts(new Date(iso)).reduce((a, p) => {
      a[p.type] = p.value; return a;
    }, {});
    // Intl gives hour "24" at midnight in en-CA — normalize.
    const h = parts.hour === "24" ? "00" : parts.hour;
    return `${parts.year}-${parts.month}-${parts.day}T${h}:${parts.minute}`;
  } catch { return ""; }
}
